import React from 'react';
import Button from 'react-bootstrap/Button';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import Confirm from '../../confirm/Confirm';
import AsyncActionButton from '../../button/AsyncActionButton';
import UnenrollSamlUsersService from '../../../service/ConvertSamlUsersService';
import { buildFailureStatus, buildStatus, buildSuccessStatus, } from '../../constants/AlertConstants';

const REPORT_HEADERS = [
  { value: 'email', display: 'Email', },
  { value: 'result', display: 'Result', },
];

const RESULT_HEADERS = [
  { value: 'email', display: 'Email', },
  { value: 'result', display: 'Reset Email Sent', },
];

const RESULT_LABELS = {
  converted: 'Unenrolled from SAML',
  notFound: 'Did not exist',
  notSaml: 'Was not SAML',
};

class UnenrollSamlUsersView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      emailsText: '',
      report: null,
      convertedEmails: [],
      sendResults: null,
      isConverting: false,
      isSending: false,
      confirmOpen: false,
      showFailedEmails: false,
      reportVersion: 0,
      sendResultsVersion: 0,
      status: null,
    };
  }

  onDismiss() {
    return () => this.setState({ status: null, });
  }

  parseEmails() {
    const { emailsText, } = this.state;
    const seen = new Set();
    const emails = [];
    emailsText
      .split(/[\n,;]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .forEach((email) => {
        const key = email.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          emails.push(email);
        }
      });
    return emails;
  }

  buildReportRows() {
    const { report, showFailedEmails, } = this.state;
    if (!report) {
      return [];
    }
    const rows = [];
    Object.keys(report.converted || {}).forEach((email) => {
      rows.push({ value: [ email, RESULT_LABELS.converted, ], raw: { email, }, });
    });
    if (showFailedEmails) {
      (report.notFound || []).forEach((email) => {
        rows.push({ value: [ email, RESULT_LABELS.notFound, ], raw: { email, }, });
      });
      (report.notSaml || []).forEach((email) => {
        rows.push({ value: [ email, RESULT_LABELS.notSaml, ], raw: { email, }, });
      });
    }
    return rows;
  }

  buildResultRows() {
    const { sendResults, } = this.state;
    if (!sendResults) {
      return [];
    }
    return sendResults.map(({ email, success, }) => ({
      value: [ email, success ? 'Yes' : 'Failed', ],
      raw: { email, },
    }));
  }

  unenrollSamlUsers() {
    const emails = this.parseEmails();
    if (emails.length === 0) {
      this.setState({
        status: buildFailureStatus('Please enter at least one email.', this.onDismiss()),
      });
      return;
    }
    this.setState({
      isConverting: true,
      report: null,
      convertedEmails: [],
      status: null,
      sendResults: null,
    });
    UnenrollSamlUsersService.unenrollSamlUsers(emails)
      .then((response) => {
        const report = response.data || {};
        const convertedEmails = Object.keys(report.converted || {});
        const message = `Processed ${emails.length} ${emails.length === 1 ? 'email' : 'emails'}. `
          + `${convertedEmails.length} ${convertedEmails.length === 1 ? 'user was' : 'users were'} unenrolled from SAML.`;
        const status = convertedEmails.length > 0
          ? buildSuccessStatus(message, this.onDismiss())
          : buildStatus(message, {
            alertVariant: 'warning',
            buttonVariant: 'outline-warning',
            header: 'No Changes',
            onDismiss: this.onDismiss(),
          });
        this.setState(previousState => ({
          report,
          convertedEmails,
          showFailedEmails: convertedEmails.length === 0,
          reportVersion: previousState.reportVersion + 1,
          status,
        }));
      })
      .catch(() => {
        this.setState({
          status: buildFailureStatus('Unable to unenroll users from SAML.', this.onDismiss()),
        });
      })
      .finally(() => {
        this.setState({ isConverting: false, });
      });
  }

  sendResetEmails() {
    const { convertedEmails, } = this.state;
    this.setState({ isSending: true, });
    UnenrollSamlUsersService.sendResetEmails(convertedEmails)
      .then((response) => {
        const { results = [], } = response.data || {};
        const failures = results.filter(result => !result.success).length;
        const status = failures === 0
          ? buildSuccessStatus(
            `Reset emails sent to ${results.length} ${results.length === 1 ? 'user' : 'users'}.`,
            this.onDismiss()
          )
          : buildFailureStatus(
            `Sent with ${failures} ${failures === 1 ? 'failure' : 'failures'}. See results below.`,
            this.onDismiss()
          );
        this.setState(previousState => ({
          sendResults: results,
          sendResultsVersion: previousState.sendResultsVersion + 1,
          status,
        }));
      })
      .catch(() => {
        this.setState({
          status: buildFailureStatus('Unable to send password reset emails.', this.onDismiss()),
        });
      })
      .finally(() => {
        this.setState({ isSending: false, confirmOpen: false, });
      });
  }

  render() {
    const {
      emailsText,
      report,
      convertedEmails,
      sendResults,
      isConverting,
      isSending,
      confirmOpen,
      showFailedEmails,
      reportVersion,
      sendResultsVersion,
      status,
    } = this.state;
    const gridStyle = { maxHeight: '400px', };

    return (
      <div className="container" style={{ marginTop: '30px', }}>
        <FieldSet title="Enter emails (one per line, or comma-separated)">
          <textarea
            className="form-control"
            rows={8}
            placeholder="user1@example.com&#10;user2@example.com"
            value={emailsText}
            onChange={event => this.setState({ emailsText: event.target.value, })}
          />
          <br />
          <AsyncActionButton
            onClick={() => this.unenrollSamlUsers()}
            contents="Unenroll from SAML"
            isLoading={isConverting}
            variant="primary"
            loadingVariant="secondary"
          />
        </FieldSet>

        {status && <div style={{ marginTop: '30px', }}>{status}</div>}

        {report && (
          <div style={{ marginTop: '30px', }}>
            <FieldSet title="SAML Unenrollment Report" style={gridStyle}>
              <label htmlFor="show-failed-emails">
                <input
                  id="show-failed-emails"
                  type="checkbox"
                  checked={showFailedEmails}
                  onChange={event => this.setState({
                    showFailedEmails: event.target.checked,
                  })}
                />
                <span style={{ marginLeft: '5px', }}>Show failed emails</span>
              </label>
              <Grid
                key={`${reportVersion}-${showFailedEmails}`}
                headers={REPORT_HEADERS}
                rows={this.buildReportRows()}
                onEmptyGridMessage="No results to show"
              />
            </FieldSet>
            {convertedEmails.length > 0 && (
              <div style={{ marginTop: '20px', }}>
                <Button variant="primary" onClick={() => this.setState({ confirmOpen: true, })}>
                  {`Send Password Reset Emails (${convertedEmails.length})`}
                </Button>
              </div>
            )}
          </div>
        )}

        {sendResults && (
          <div style={{ marginTop: '30px', }}>
            <FieldSet title="Reset Email Results" style={gridStyle}>
              <Grid
                key={sendResultsVersion}
                headers={RESULT_HEADERS}
                rows={this.buildResultRows()}
                onEmptyGridMessage="No results to show"
              />
            </FieldSet>
          </div>
        )}

        <Confirm
          isLoading={isSending}
          title="Send Password Reset Emails"
          content={(
            <>
              {`Send a password-reset email to ${convertedEmails.length} affected `
                + `${convertedEmails.length === 1 ? 'user' : 'users'}?`}
              <details style={{ marginTop: '16px', }}>
                <summary>View affected users</summary>
                <ul style={{ marginBottom: 0, paddingLeft: '24px', }}>
                  {convertedEmails.map(email => <li key={email}>{email}</li>)}
                </ul>
              </details>
            </>
          )}
          open={confirmOpen}
          onClose={() => this.setState({ confirmOpen: false, })}
          onYes={() => this.sendResetEmails()}
          onNo={() => this.setState({ confirmOpen: false, })}
        />

      </div>
    );
  }
}

export default UnenrollSamlUsersView;
